import Cookies from 'js-cookie'
import { currentLocationTarget, loginUrl } from './utils/loginRedirect'

const isDev = import.meta.env.DEV

// Must match the domain used when the cookie is set (see src/main.jsx / src/pages/Login)
export const cookieDomain = isDev ? 'localhost' : 'admin.app.horoluma.com'

/**
 * HOR-21: Logout/Benutzerwechsel blieb in Chrome wirkungslos.
 *
 * `Cookies.remove` loescht nur genau das Tripel (Name, Domain, Path). Ein
 * Session-Cookie, das irgendwann ohne Domain-Attribut gesetzt wurde - also
 * host-only - ueberlebt ein Remove mit Domain unbeschadet. Beim naechsten
 * Seitenaufruf las main.jsx es wieder ein und der Nutzer war weiter angemeldet.
 *
 * Deshalb: alle plausiblen Varianten abraeumen und anschliessend pruefen, ob
 * wirklich nichts uebrig ist. Nur der letzte Schritt ist neu-defensiv, der Rest
 * kostet nichts.
 */
const removeSessionCookieEverywhere = () => {
  const variants = [{ path: '/' }, { domain: cookieDomain, path: '/' }, { domain: `.${cookieDomain}`, path: '/' }]

  variants.forEach(options => {
    try {
      Cookies.remove('session', options)
    } catch (e) {
      // Ungueltige Domain-Kombination (z.B. localhost): naechste Variante versuchen.
    }
  })
}

/**
 * HOR-21: Setzen und Loeschen des Session-Cookies gehoeren zusammen an eine
 * Stelle - stimmen Domain oder Path nicht exakt ueberein, laesst sich das
 * Cookie spaeter nicht mehr entfernen. `path` steht bewusst explizit da, auch
 * wenn js-cookie ohnehin '/' verwendet.
 */
export const setSession = session => {
  Cookies.set('session', JSON.stringify(session), {
    domain: cookieDomain,
    path: '/',
    expires: 365,
  })
}

export const clearSession = () => {
  removeSessionCookieEverywhere()

  // Letzter Ausweg: direkt ueberschreiben und in die Vergangenheit datieren.
  if (Cookies.get('session') !== undefined) {
    document.cookie = 'session=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/'
    document.cookie = `session=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${cookieDomain}`
  }

  if (global.WatchStorage) global.WatchStorage.session = null

  return Cookies.get('session') === undefined
}

/**
 * Central fetch wrapper for all API calls.
 * Never throws. Always resolves to: { ok, status, data, errorType }
 * errorType ∈ null | 'unauthorized' | 'forbidden' | 'server' | 'network'
 *
 * On 401 the session is invalidated and the user is redirected to the login
 * page with ?reason=sessionExpired.
 */
export const apiFetch = async (path, { method = 'GET', body } = {}) => {
  let response

  try {
    response = await fetch(`${global.WatchStorage.backendUrl}/api/v1${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(global.WatchStorage.session?.token ? { Authorization: `Bearer ${global.WatchStorage.session.token}` } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
  } catch (e) {
    return { ok: false, status: null, data: null, errorType: 'network' }
  }

  let data = null

  try {
    data = await response.json()
  } catch (e) {
    // empty or non-JSON body – keep data = null
  }

  const { status } = response

  if (status === 401) {
    clearSession()
    // HOR-92 (F1): aktuelle Seite als ?redirect= mitgeben, damit der Admin nach dem erneuten
    // Login dort weitermacht, wo die Sitzung abgelaufen ist (nur relative Pfade, s. loginRedirect).
    window.location.replace(loginUrl({ reason: 'sessionExpired', redirect: currentLocationTarget() }))
    return { ok: false, status, data, errorType: 'unauthorized' }
  }

  if (status === 403) return { ok: false, status, data, errorType: 'forbidden' }

  if (!response.ok) return { ok: false, status, data, errorType: 'server' }

  return { ok: true, status, data, errorType: null }
}
